import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { map, tap, catchError, delay } from "rxjs/operators";
import { withErrorHandler } from './error-handler.util';
import { environment } from "../../environments";
import { Site, Tag } from "../interfaces/sites";
import { Store } from "@tauri-apps/plugin-store";
import { Native } from "../../native";
import { emit } from "@tauri-apps/api/event"
import Cookies from "../utils/cookies";

export interface SiteHelper{
  sites:Site[]
  sitesMap:{ [key: number]: Site }
  expiry?: Number
}

async function saveToTauriStore(key: string, value: any) {
  if (!Native.IsNative()) return;

  try {
    const store = await Store.load("config.json");
    
    await store.set(key, value);
    await store.save();
  
    await new Promise(resolve => setTimeout(resolve, 50));
    await emit("store-updated", { key: key });
    
  } catch (error) {
    console.error("Tauri store operation failed:", error);
  }
}

@Injectable({
  providedIn: "root",
})
export class SiteService {
  sitesHelper: SiteHelper = null;
  baseURL = environment.API_URL + "site/";

  constructor(private http: HttpClient) {}

  getSites(admin:boolean = false): Observable<Site[]> {
    return this.http.get<Site[]>(this.baseURL + "get-sites", {
      params: {
        admin: admin.toString()
      }
    }).pipe(
      withErrorHandler({ operation: admin ? 'getSitesAdmin' : 'getSites', cache: true, fallback: [] }),
      map((e) => {
        const u = e.filter((site) => {
          return site.name != "888Poker(ES-PT)"
        })
        return u
      })
    );
  }

  getSitesHelpers(admin: boolean = false): Observable<SiteHelper> {
    const now = Date.now();
    const cacheKey = 'sites-' + admin;
    const cached = localStorage.getItem(cacheKey);
    let cachedHelper: SiteHelper | null = null;

    if (cached) {
      try {
        cachedHelper = JSON.parse(cached);
      } catch {
        localStorage.removeItem(cacheKey);
      }
    }

    if (cachedHelper && cachedHelper.expiry && now < Number(cachedHelper.expiry)) {
      this.sitesHelper = cachedHelper;
      saveToTauriStore(cacheKey, this.sitesHelper);
      return of(this.sitesHelper);
    }

    return this.getSites(admin).pipe(
      map((res) => {
        // WORKAROUND - data that comes from getSites() is different from Site interface
        res.forEach((site) => {
          site.tags.forEach((tag) => {
            tag["tag"] = tag["name"];
            delete tag["name"];
          });
        });
        // END OF WORKAROUND
        let sites = res;
        sites.forEach((site) => {
          site.tags.sort((a, b) => a.priority - b.priority);
        });
        let sitesMap = {};
        res.forEach((site) => {
          sitesMap[site.id] = site;
        });
        this.sitesHelper = { sites, sitesMap, expiry: now + 60 * 1000 }; // 1 minute
        localStorage.setItem(cacheKey, JSON.stringify(this.sitesHelper));
        saveToTauriStore(cacheKey, this.sitesHelper);
        return this.sitesHelper;
      })
    );
  }

  // Favorites can change outside this cache's TTL window, so drop it instead of risking stale data.
  clearSitesCache(): void {
    this.sitesHelper = null;
    ['sites-true', 'sites-false'].forEach((cacheKey) => {
      localStorage.removeItem(cacheKey);
      saveToTauriStore(cacheKey, null);
    });
  }

  createSiteTag(siteID: number, body: Tag): Observable<Tag> {
    return this.http.put<Tag>(this.baseURL + "create-site-tag/" + siteID, body).pipe(
      withErrorHandler({ operation: 'createSiteTag', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('createSiteTag failed:', err);
        return throwError(() => err);
      })
    );
  }

  updateTag(tagID: number, body: Tag): Observable<Tag> {
    return this.http.put<Tag>(this.baseURL + "update-tag/" + tagID, body).pipe(
      withErrorHandler({ operation: 'updateTag', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('updateTag failed:', err);
        return throwError(() => err);
      })
    );
  }

  deleteTag(tagID: number): Observable<Tag> {
    return this.http.delete<Tag>(this.baseURL + "delete-tag/" + tagID).pipe(
      withErrorHandler({ operation: 'deleteTag', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('deleteTag failed:', err);
        return throwError(() => err);
      })
    );
  }

  updateSite(siteID: number, body: any): Observable<Site> {
    return this.http.put<Site>(this.baseURL + "update-site/" + siteID, body).pipe(
      withErrorHandler({ operation: 'updateSite', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('updateSite failed:', err);
        return throwError(() => err);
      })
    );
  }

  createSite(body: any) {
    return this.http.post(this.baseURL + "create-site", body).pipe(
      withErrorHandler({ operation: 'createSite', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('createSite failed:', err);
        return throwError(() => err);
      })
    );
  }

  getSeriesTag(): Observable<any> {
    return this.http.get<any>(this.baseURL + "get-series-tags").pipe(
      withErrorHandler({ operation: 'getSeriesTag', cache: true, fallback: [] })
    );
  }

  updateSeriesTag(siteID: number, body: any): Observable<any> {
    return this.http.put<any>(this.baseURL + "create-series-tag/" + siteID, body).pipe(
      withErrorHandler({ operation: 'updateSeriesTag', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('updateSeriesTag failed:', err);
        return throwError(() => err);
      })
    );
  }

  deleteSeriesTag(tagID: number): Observable<Tag> {
    return this.http.delete<Tag>(this.baseURL + "delete-series-tag/" + tagID).pipe(
      withErrorHandler({ operation: 'deleteSeriesTag', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('deleteSeriesTag failed:', err);
        return throwError(() => err);
      })
    );
  }

  getSitesLabels() {
    return this.http.get<any>(this.baseURL + "get-label", {
      params: {
        'with_id': '1',
      }
    }).pipe(
      withErrorHandler({ operation: 'getSitesLabels', cache: true, fallback: [] })
    ).toPromise();
  }

  saveSiteLabels(body: {
    id?: number,
    brand: string,
    label: string,
    site: number,
  }) {
    return this.http.put<HttpResponse<any>>(this.baseURL + "update-label", body).pipe(
      withErrorHandler({ operation: 'saveSiteLabels', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('saveSiteLabels failed:', err);
        return throwError(() => err);
      })
    ).toPromise();
  }

  deleteSiteLabel(id: number) {
    return this.http.post<HttpResponse<void>>(this.baseURL + "delete-label", {
      id,
    }).pipe(
      withErrorHandler({ operation: 'deleteSiteLabel', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('deleteSiteLabel failed:', err);
        return throwError(() => err);
      })
    ).toPromise();
  }
}
